import { motion, useReducedMotion } from "motion/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { CAPTAIN_BILLING_CHANGE_TYPES, CAPTAIN_BILLING_HISTORY_PREFIX, CaptainBillingHistoryItem } from "./CaptainBillingHistoryItem";
import { HistorySourceContext, isHistorySource } from "./historySource";
import { useNewEntryKeys } from "./useNewEntryKeys";
import { useCaptainBillingHistory, usePrefetchCaptainBillingHistory } from "../../api/useCaptainBillingHistory";
import { HISTORY_SOURCES } from "../../constants";
import type { CaptainBillingChangeType, CaptainBillingHistoryEntry, CaptainBillingHistorySource } from "../../types";
import { BALANCE_EASE } from "../AnimatedNumber";

import { type FilterSectionConfig, FiltersDrawer } from "@/components/ui/v2/filters/FiltersDrawer";
import { CHANGE_TYPE_PARAM, HistoryTimeline, HistoryToolbar, useHistoryUrlFilters } from "@/components/ui/v2/history";
import type { ActiveFilter } from "@/components/ui/v2/table/table-active-filters";
import Tabs from "@/components/ui/v2/tabs";
import { URL_PARAMS } from "@/constants/urlParams";
import { useSearchSearchParams } from "@/lib/hooks/useSearchSearchParams";
import { cn } from "@/lib/utils/cn";

const DEFAULT_DAYS = 30;
const PAGE_SIZE = 50;
const SOURCE_PARAM = "source";
const ACTOR_PARAM = "actor";
const MODEL_PARAM = "model";
const SYSTEM_ACTOR = "system";
const ALL_TAB = "all";
const TABS = [ALL_TAB, ...HISTORY_SOURCES] as const;

const TAB_TRANSITION = { duration: 0.3, ease: BALANCE_EASE } as const;

const isChangeType = (value: string): value is CaptainBillingChangeType =>
  (CAPTAIN_BILLING_CHANGE_TYPES as string[]).includes(value);

type CaptainBillingHistoryProps = {
  clientId: string;
  canManage: boolean;
};

export function CaptainBillingHistory({ clientId, canManage }: CaptainBillingHistoryProps) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceParam = searchParams.get(SOURCE_PARAM);
  const source = isHistorySource(sourceParam) ? sourceParam : undefined;

  const handleSourceChange = (tabId: string | undefined) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (isHistorySource(tabId ?? null)) next.set(SOURCE_PARAM, tabId!);
        else next.delete(SOURCE_PARAM);
        next.delete(CHANGE_TYPE_PARAM);
        return next;
      },
      { replace: true },
    );

  return (
    <section aria-labelledby="captain-billing-history-title" className="space-y-3">
      <div className="space-y-1">
        <h2 id="captain-billing-history-title" className="text-lg font-semibold text-foreground">
          {t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.title`)}
        </h2>
        <p className="text-sm text-muted-foreground">
          {t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.subtitle.${canManage ? "admin" : "manager"}`)}
        </p>
      </div>
      <Tabs
        activeTabId={source ?? ALL_TAB}
        onChange={(tab) => handleSourceChange(tab.id)}
        tabs={TABS.map((tab) => ({ id: tab, label: t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.tabs.${tab}`), content: null }))}
      />
      <HistoryList clientId={clientId} source={source} canManage={canManage} />
    </section>
  );
}

type HistoryListProps = Pick<CaptainBillingHistoryProps, "clientId" | "canManage"> & {
  source?: CaptainBillingHistorySource;
};

function HistoryList({ clientId, source, canManage }: HistoryListProps) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filters = useHistoryUrlFilters({ defaultDays: DEFAULT_DAYS });
  const { searchValue, debouncedSearchValue, setSearchValue } = useSearchSearchParams();
  const changeTypes = filters.changeTypes.filter(isChangeType);
  const actorParam = searchParams.getAll(ACTOR_PARAM);
  const actorKey = actorParam.join(",");
  const actors = useMemo(() => (actorKey ? actorKey.split(",") : []), [actorKey]);
  const search = debouncedSearchValue?.trim() || undefined;
  const modelKey = searchParams.getAll(MODEL_PARAM).join(",");
  const models = useMemo(() => (modelKey ? modelKey.split(",") : []), [modelKey]);

  const period = {
    from: filters.dateRange.from?.toISOString(),
    to: filters.hasEndDate ? filters.dateRange.to?.toISOString() : undefined,
    pageSize: PAGE_SIZE,
  };
  const facets = {
    search,
    actors: actors.length > 0 ? actors : undefined,
    models: models.length > 0 ? models : undefined,
  };
  const historyResult = useCaptainBillingHistory(clientId, {
    source,
    ...period,
    ...facets,
    changeTypes: changeTypes.length > 0 ? changeTypes : undefined,
  });
  const settled = historyResult.isSuccess && !historyResult.isPlaceholderData;
  usePrefetchCaptainBillingHistory(clientId, { source, ...period, ...facets }, settled);

  const rows = useMemo(() => historyResult.data?.pages.flatMap((page) => page.data) ?? [], [historyResult.data]);
  const { shownSource, isNew } = useNewEntryKeys(rows, source, settled);
  const firstPage = historyResult.data?.pages[0];
  const pending = historyResult.isPlaceholderData;

  const changeTypeLabel = (type: string) => t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.changeTypes.${type}`, { defaultValue: type });
  const actorLabel = (actor: string, name?: string | null) =>
    actor === SYSTEM_ACTOR ? t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.system`) : (name ?? t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.unknownActor`));
  const actorNames = new Map((firstPage?.actorCounts ?? []).map((count) => [count.actor, count.name]));
  const filterSections: FilterSectionConfig[] = [
    {
      key: CHANGE_TYPE_PARAM,
      title: t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.filters.event`),
      options: CAPTAIN_BILLING_CHANGE_TYPES.flatMap((type) => {
        const count = firstPage?.changeTypeCounts.find((c) => c.changeType === type)?.count;
        return count ? [{ value: type, label: changeTypeLabel(type), count }] : [];
      }),
    },
    {
      key: ACTOR_PARAM,
      title: t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.filters.actor`),
      options: (firstPage?.actorCounts ?? []).map((count) => ({
        value: count.actor,
        label: actorLabel(count.actor, count.name),
        count: count.count,
      })),
    },
    ...(firstPage?.modelCounts.length
      ? [
        {
          key: MODEL_PARAM,
          title: t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.filters.model`),
          options: firstPage.modelCounts.map((count) => ({ value: count.model, label: count.model, count: count.count })),
        },
      ]
      : []),
  ];
  const activeFilters: ActiveFilter[] = [
    ...changeTypes.map((type) => ({
      key: CHANGE_TYPE_PARAM,
      label: t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.filters.event`),
      value: type,
      valueLabel: changeTypeLabel(type),
    })),
    ...actors.map((actor) => ({
      key: ACTOR_PARAM,
      label: t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.filters.actor`),
      value: actor,
      valueLabel: actorLabel(actor, actorNames.get(actor)),
    })),
    ...models.map((model) => ({
      key: MODEL_PARAM,
      label: t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.filters.model`),
      value: model,
      valueLabel: model,
    })),
  ];
  const updateFilterParams = (change: (next: URLSearchParams) => void) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        change(next);
        return next;
      },
      { replace: true },
    );
  const removeFilter = (filter: ActiveFilter) =>
    updateFilterParams((next) => {
      const kept = next.getAll(filter.key).filter((value) => value !== filter.value);
      next.delete(filter.key);
      kept.forEach((value) => next.append(filter.key, value));
    });
  const clearFilters = () =>
    updateFilterParams((next) => {
      next.delete(CHANGE_TYPE_PARAM);
      next.delete(ACTOR_PARAM);
      next.delete(MODEL_PARAM);
    });

  return (
    <HistorySourceContext.Provider value={shownSource}>
      <motion.div
        key={shownSource ?? ALL_TAB}
        initial={reduceMotion ? false : { opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={TAB_TRANSITION}
        aria-busy={pending}
        data-testid="captain-billing-history-list"
        className={cn("min-h-96 transition-opacity", pending ? "opacity-60 delay-150 duration-200" : "duration-150")}
      >
        <HistoryTimeline
          useHistoryResult={historyResult}
          renderEntry={(entry) => (
            <EntryAppear isNew={isNew(entry as CaptainBillingHistoryEntry)}>
              <CaptainBillingHistoryItem entry={entry as CaptainBillingHistoryEntry} />
            </EntryAppear>
          )}
          i18nPrefix={CAPTAIN_BILLING_HISTORY_PREFIX}
          inset="tab"
          toolbar={
            <HistoryToolbar
              i18nPrefix={CAPTAIN_BILLING_HISTORY_PREFIX}
              search={{
                value: searchValue ?? "",
                onChange: setSearchValue,
                placeholder: t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.searchPlaceholder.${canManage ? "admin" : "manager"}`),
              }}
              totalCount={firstPage?.totalRecords}
              dateRange={filters.dateRange}
              onDateRangeChange={filters.onDateRangeChange}
              onFiltersClick={() => setFiltersOpen(true)}
              activeFilters={activeFilters}
              onRemoveFilter={removeFilter}
              onClearFilters={clearFilters}
            />
          }
        />
      </motion.div>
      <FiltersDrawer
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        searchParams={searchParams}
        filterKeys={[CHANGE_TYPE_PARAM, ACTOR_PARAM, MODEL_PARAM]}
        filterSections={filterSections}
        entityName={t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.title`)}
        totalCount={firstPage?.totalRecords}
        searchTag={search}
        preserveParamKeys={[SOURCE_PARAM, URL_PARAMS.SEARCH, URL_PARAMS.DATE_FROM, URL_PARAMS.DATE_TO]}
        resetsPage={false}
        defaultExpanded
      />
    </HistorySourceContext.Provider>
  );
}

function EntryAppear({ isNew, children }: { isNew: boolean; children: React.ReactNode }) {
  const reduceMotion = useReducedMotion();
  const animateIn = isNew && !reduceMotion;

  return (
    <motion.div
      data-new-entry={animateIn || undefined}
      className="relative overflow-hidden rounded-lg"
      initial={animateIn ? { opacity: 0, height: 0, y: -8 } : false}
      animate={{ opacity: 1, height: "auto", y: 0 }}
      transition={{ duration: 0.45, ease: BALANCE_EASE }}
    >
      {children}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-lg ring-2 ring-primary ring-inset"
        initial={{ opacity: animateIn ? 0.6 : 0 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 1.6, delay: 0.3, ease: BALANCE_EASE }}
      />
    </motion.div>
  );
}
